import * as React from 'react';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {MarketingOutreachSelectList} from 'shells/marketing-outreach-select-list/marketing-outreach-select-list';
import {MarketingOutreachSelectListEmptyState} from 'shells/marketing-outreach-select-list/marketing-outreach-select-list-empty-state';
import {FooterBar} from 'shells/modal/footer-bar';
import {SearchBar} from 'shells/search-bar/search-bar';

import type {EditionStub as Edition} from 'nutshell-graphql-types';

import {getSendEditionFromListDisabledReasons} from './send-marketing-outreach-helpers';
import type {OutreachItem, DripSequence} from './send-marketing-outreach-modal';

import styles from './select-marketing-outreach-list-page.module.css';

const DRIP_SEQUENCES_LIST_ID = 'drip-sequences';

type Props = {
    // When the "Cancel" button is clicked
    onClickCancel: () => void;
    // When the "Next" button is clicked
    onSelectOutreachItem: (selectedItem: OutreachItem) => void;
    // Drafts (ready for sending)
    draftEditions: Edition[] | null | undefined;
    isLoadingDraftEditions: boolean;
    errorLoadingDraftEditions: string | null | undefined;
    // Sent editions
    sentEditions: Edition[] | null | undefined;
    isLoadingSentEditions: boolean;
    errorLoadingSentEditions: string | null | undefined;
    // Active drip sequences
    activeDripSequences: DripSequence[] | null | undefined;
    isLoadingActiveDripSequences: boolean;
    errorLoadingActiveDripSequences: string | null | undefined;
    canAccessFullCampaigns: boolean;
};

export function SelectMarketingOutreachListPage(props: Props) {
    const [selectedItemId, setSelectedItemId] = React.useState<string | null | undefined>(
        undefined
    );
    const [searchValue, setSearchValue] = React.useState<string>('');

    const searchValuePattern = new RegExp(searchValue, 'i');

    // Drafts editions - if not ready to send are disabled
    const draftEditions = props.draftEditions
        ? props.draftEditions
              .filter((edition) => searchValuePattern.test(edition.name))
              .map((edition) => {
                  const errorMessages = getSendEditionFromListDisabledReasons(edition);

                  return {
                      id: edition.id,
                      edition: {
                          ...edition,
                          thumbnailUrl:
                              edition.emailTemplate && edition.emailTemplate.thumbnailUrl
                                  ? edition.emailTemplate.thumbnailUrl
                                  : undefined,
                          campaignType: edition.campaign.type,
                          htmlUrlPath: edition.htmlUrlPath,
                      },
                      disabledReasons: errorMessages,
                  };
              })
              .sort((a, b) => {
                  if (!a.disabledReasons || !b.disabledReasons) {
                      return 0;
                  }

                  if (a.disabledReasons.length === b.disabledReasons.length) {
                      return 0;
                  }

                  return b.disabledReasons.length > a.disabledReasons.length ? -1 : 1;
              })
        : undefined;

    // Sent editions
    const sentEditions = props.sentEditions
        ? props.sentEditions
              .filter((edition) => searchValuePattern.test(edition.name))
              .map((edition) => {
                  return {
                      id: edition.id,
                      edition: {
                          ...edition,
                          thumbnailUrl:
                              edition.emailTemplate && edition.emailTemplate.thumbnailUrl
                                  ? edition.emailTemplate.thumbnailUrl
                                  : undefined,
                          campaignType: edition.campaign.type,
                      },
                  };
              })
        : undefined;

    // Drip sequences
    const activeDripSequences = props.activeDripSequences
        ? props.activeDripSequences
              .filter((dripSequence) => searchValuePattern.test(dripSequence.name))
              .map((dripSequence) => ({
                  id: dripSequence.id,
                  dripSequence: {
                      id: dripSequence.id,
                      name: dripSequence.name,
                      startTrigger: dripSequence.startTrigger,
                      lastStatusChangeTime: dripSequence.lastStatusChangeTime,
                      createdTime: dripSequence.createdTime,
                      status: dripSequence.status,
                      editions: dripSequence.editions.map((edition) => ({
                          id: edition.id,
                          name: edition.name,
                          comparison: null,
                          backgroundColor: edition.backgroundColor,
                          secondaryInfo: edition.emailTemplate ? (
                              edition.emailTemplate.subject
                          ) : (
                              <div style={{fontStyle: 'italic'}}>No message</div>
                          ),
                      })),
                  },
              }))
        : undefined;

    let categorizedLists = [
        {
            id: 'draft-editions',
            title: 'Drafts',
            subtitle: 'Messages that are ready to send',
            collection: draftEditions,
            error: props.errorLoadingDraftEditions,
            isLoading: props.isLoadingDraftEditions,
            emptyStateComponent: (
                <MarketingOutreachSelectListEmptyState emptyStateVariant='draft-editions' />
            ),
        },
        {
            id: 'sent-editions',
            title: 'Sent',
            subtitle: 'Messages that have been sent out',
            collection: sentEditions,
            error: props.errorLoadingSentEditions,
            isLoading: props.isLoadingSentEditions,
            emptyStateComponent: (
                <MarketingOutreachSelectListEmptyState emptyStateVariant='sent-editions' />
            ),
        },
        {
            id: DRIP_SEQUENCES_LIST_ID,
            title: 'Drip sequences',
            subtitle: 'A series of messages to kick off',
            collection: activeDripSequences,
            error: props.errorLoadingActiveDripSequences,
            isLoading: props.isLoadingActiveDripSequences,
            emptyStateComponent: (
                <MarketingOutreachSelectListEmptyState emptyStateVariant='drip-sequences' />
            ),
            customFillRule: 'repeat(auto-fit, minmax(460px, 1fr))',
        },
    ];

    if (!props.canAccessFullCampaigns) {
        categorizedLists = categorizedLists.filter((list) => list.id !== DRIP_SEQUENCES_LIST_ID);
    }

    return (
        <div className={styles['select-marketing-outreach-list-page']}>
            <div className={styles['search-container']}>
                <SearchBar
                    value={searchValue}
                    onChange={(val) => setSearchValue(val)}
                    isClearable={true}
                    placeholder='Search for a message…'
                />
            </div>
            <div className={styles['outreach-list-container']}>
                <MarketingOutreachSelectList
                    categorizedLists={categorizedLists}
                    selectedItemId={selectedItemId}
                    setSelectedItemId={setSelectedItemId}
                    isFiltering={Boolean(searchValue)}
                />
            </div>
            <FooterBar
                left={
                    <ButtonMover size='big' direction='left'>
                        <Button onClick={props.onClickCancel} variant='text-secondary' size='big'>
                            Cancel
                        </Button>
                    </ButtonMover>
                }
                right={
                    <Button
                        onClick={() => {
                            if (selectedItemId) {
                                const allItems = [];
                                if (props.draftEditions) {
                                    allItems.push(...props.draftEditions);
                                }
                                if (props.sentEditions) {
                                    allItems.push(...props.sentEditions);
                                }
                                if (props.activeDripSequences) {
                                    allItems.push(...props.activeDripSequences);
                                }

                                const selectedItem = allItems.find(
                                    (item: OutreachItem) => item.id === selectedItemId
                                );

                                if (selectedItem) {
                                    props.onSelectOutreachItem(selectedItem);
                                }
                            }
                        }}
                        disabled={!selectedItemId}
                        variant='primary'
                        size='big'
                    >
                        Next
                    </Button>
                }
            />
        </div>
    );
}
